/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect, useCallback } from "react";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
// node.js library that concatenates classes (strings)
import classnames from "classnames";
import moment from "moment";

import {
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Container,
  Row,
  Col,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
} from "reactstrap";

import Header from "../../components/Headers/Header";
import Column from "../../components/Charts/Column";
import RadialBar from "../../components/Charts/RadialBar";
import AreaChart from "../../components/Charts/AreaChart";
import PairedColumnChart from "../../components/Charts/PairedColumnChart";
import DashboardStatCard from "../../components/Cards/Dashboard/DashboardStatCard";
import DotsSpinner from "../../components/Loader/DotsSpinner/DotsSpinner";

import {
  getAllStudentsForParticularGradeSection,
  getAppUsagesData,
  getGradeSectionWiseAppUsagesData,
  getGradeSectionWisePerformance,
  getGradeWiseOverallProgress,
  getStudentCountForTeacher,
  getTeacherCount
} from "../../Services/Stats/StatService";
import { groupBy } from "../../Services/utils";
import useTeacherProfile from "../../Hooks/useTeacherProfile";
import { TypeTeacherSchool } from "../../Reducers/types";
import { TypeAppUsage, TypeDashboardStats, typeGradeSection, TypeGradeWisePerformane } from "../../types";
import { logOut } from "../../Services/UserAuth/UserAuthenticate";
import AppUsage from "../../components/Custom/AppUsage";

const Index = (props) => {

  const teacherProfile: TypeTeacherSchool = useTeacherProfile(); // stores teacher profile information

  // --- DROPDOWN GRADE ---
  const [gradeDropdownOpen, setGradeDropdownOpen] = useState(false);
  const gradeDropdownToggle = () => setGradeDropdownOpen(prevState => !prevState);

  // --- GRADE-SECTION-SUBJECT ---
  const [gradeSectionInfo, setGradeSectionInfo] = useState<typeGradeSection[]>([]); // stores grade-section information
  const [selectedGrade, setSelectedGrade] = useState<typeGradeSection>(); // stores selected grade

  // *** STATS ***
  const [statCards, setStatCards] = useState<TypeDashboardStats>(); // holds stats for header cards
  const [appUsage, setAppUsage] = useState<TypeAppUsage[]>(); // stores app usage data
  const [appUsageChartData, setAppUsageChartData] = useState<{ labels: string[], data: number[] }>(); // stores app usage data to create a line chart
  const [gradeWisePerformance, setGradeWisePerformance] = useState<TypeGradeWisePerformane>(); // stores grade wise performance
  const [overallPerformance, setOverallPerformance] = useState<number[]>(); // stores overall performance for a grade 
  const [isStatLoading, setIsStatLoading] = useState<boolean>(false); // for stat loading
  const [isPerformanceLoading, setIsPerformanceLoading] = useState<boolean>(false); // for loading performance

  const [activeNav, setActiveNav] = useState(1);

  useEffect(() => {
    filterGrades();
  }, []);
  useEffect(() => {
    setSelectedGrade(gradeSectionInfo[0]); // sets selected grade-section
    // getStat();
  }, [gradeSectionInfo]);
  useEffect(() => { // fatches class performance & overall performance based on selected grade-section
    setIsPerformanceLoading(true);
    setActiveNav(1);
    getGradePerformance();
    getStat();
    getGradeWiseOverallPerformance(selectedGrade?.grade_section_id?.id, selectedGrade?.grade_section_id?.grade?.id);
  }, [selectedGrade]);

  // filters grades from teacher profile
  const filterGrades = () => {
    if (Number(teacherProfile?.gradelist.length) === 0) { toast.error("Can not get grades. Please log in again!") }
    // filters grade-section list based on current session
    setGradeSectionInfo(
      teacherProfile?.gradelist?.filter(({ grade_section_id, subject }) => {
        if (moment(new Date()).format('YYYY-MM-DD') >= moment(grade_section_id?.session?.session_start).format('YYYY-MM-DD')
          &&
          moment(new Date()).format('YYYY-MM-DD') <= moment(grade_section_id?.session?.session_end).format('YYYY-MM-DD')) {
          return { grade_section_id, subject };
        }
      })
    );
    setSelectedGrade(gradeSectionInfo[0]);
  };

  // get stats for stat cards
  const getStat = () => {
    setIsStatLoading(true);

    // fetches total number of students
    let gradeSectionList: number[] = gradeSectionInfo?.map((each) => (
      each?.grade_section_id?.id
    ));
    // console.log("Grade List: ", gradeSectionList);
    getStudentCountForTeacher(gradeSectionList).then(
      (response: AxiosResponse) => {
        console.log("Response for student count: ", response?.data?.meta?.filter_count);
        setStatCards((oldData) => ({ ...oldData, totalStudents: response?.data?.meta?.filter_count }));
      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));

    // fetches total number of teachers
    getTeacherCount().then(
      (response: AxiosResponse) => {
        // console.log("Response for teacher count: ", response?.data?.meta?.filter_count);
        setStatCards((oldData) => ({ ...oldData, totalTeachers: response?.data?.meta?.filter_count }));
      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };

  /* 
                CALCULATE APP USAGE
    get all student list for particular grade-list
  */
  // const getAllStudents = (gradeSectionId: number) => {
  //   getAllStudentsForParticularGradeSection(gradeSectionId).then(
  //     (response: AxiosResponse) => {
  //       // console.log("Response for all students: ", response?.data?.data);
  //       let student_list: number[] = response?.data?.data?.map((each) => (each?.student_profile_id?.user));
  //       getAppUse(student_list);
  //     }, (error) => {
  //       console.log("Error caught: ", error?.response);
  //     }
  //   ).catch(error => console.log("Catched error ---> ", error));
  // };
  // // get app use for particular grade-section
  // const getAppUse = (studentList: number[]) => {
  //   getGradeSectionWiseAppUsagesData(studentList).then(
  //     (response: AxiosResponse) => {
  //       // console.log("Response for app use: ", response?.data?.data);
  //       setAppUsage(response?.data?.data);
  //       calculateDaysUsage(response?.data?.data, 7);
  //     }, (error) => {
  //       console.log("Error caught: ", error?.response);
  //     }
  //   ).catch(error => console.log("Catched error ---> ", error));
  // };

  // // calculates usage for specific days(last 7 days or last 30 days)
  // const calculateDaysUsage = (usageData, days: number) => {
  //   setIsStatLoading(true);
  //   let lastSpecificDaysUsage = usageData?.filter((each) => ( // filters out data based on day - days parameter
  //     each?.usage_on >= moment().subtract(days, 'd').format('YYYY-MM-DD') && each?.usage_on <= moment().format('YYYY-MM-DD')
  //   ));
  //   // console.log(`Last ${days} days usage: `, lastSpecificDaysUsage);
  //   let groupedSpecificDaysUsage = groupBy(lastSpecificDaysUsage, 'usage_on'); // groups usage based on date

  //   let formatedSevenDaysUsage = Object.keys(groupedSpecificDaysUsage)?.map((each, index) => { // formats data based on date to get total usage for a particular day
  //     return {
  //       usageDay: Object.keys(groupedSpecificDaysUsage)[index],
  //       usage: groupedSpecificDaysUsage[each]?.reduce((accu, { time_in_min }) => accu += time_in_min, 0)
  //     }
  //   });

  //   // setting data to depict app usage chart
  //   setAppUsageChartData((oldData) => ({
  //     ...oldData,
  //     labels: formatedSevenDaysUsage?.map(({ usageDay }) => { return moment(usageDay).format('MMM Do') }),
  //     data: formatedSevenDaysUsage?.map(({ usage }) => { return usage }),
  //   }));
  //   console.log(`Last ${days} days usage: `, formatedSevenDaysUsage);
  //   setTimeout(() => setIsStatLoading(false), 500);
  // };


  /* 
    PERFORMANCE 
  */
  // get grade wise performance
  const getGradePerformance = () => {
    getGradeSectionWisePerformance(selectedGrade?.grade_section_id?.id, selectedGrade?.grade_section_id?.grade?.id).then(
      (response: AxiosResponse) => {
        // console.log("response for grade wise performance: ", response?.data);
        let label: string[] = [];
        let local_label: Set<any> = new Set();
        let local_progress: number[] = [];
        let local_performance: number[] = [];

        Object.keys(response?.data).map(e => response?.data[e]).forEach(item => item.forEach(each => {
          each.average_performance && local_performance.push(Math.round(Number(each.average_performance)));
          each.average_progress && local_progress.push(Math.round(Number(each.average_progress)));
          each.subject_name && local_label.add(each.subject_name)
        }));
        label = Array.from(local_label);
        setGradeWisePerformance((oldData) => ({ ...oldData, labels: label, performance: local_performance, progress: local_progress }));
      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error)).finally(() => setIsPerformanceLoading(false));
  };
  // get grade wise overall performance
  const getGradeWiseOverallPerformance = (grade_section_id: number, grade_id: number) => {
    getGradeWiseOverallProgress(grade_section_id, grade_id).then(
      (response: AxiosResponse) => {
        // console.log("Response for overall performance: ", response?.data);
        let overall: number[] = [Math.round(Number(response?.data?.performance?.average)), Math.round(Number(response?.data?.progress?.average))];
        setOverallPerformance(overall);
      }, (error) => {
        console.log("Error caught: ", error?.response);
      }
    ).catch(error => console.log("Catched error ---> ", error));
  };

  return (
    <React.Fragment>
      <Header />

      {/* Page content */}
      <Container className="mt--9" fluid>
        <Row>
          <Col lg="6" xl="3" sm="12">
            <DashboardStatCard title={'Total Students'} stats={statCards?.totalStudents} icon={'fas fa-users'} iconColor={'text-muted'} label={'From start till today'} />
          </Col>
          <Col lg="6" xl="3" sm="12">
            {/* <DashboardStatCard title={'Total Teachers'} stats={statCards?.totalTeachers} icon={'fas fa-chalkboard-teacher'} iconColor={'text-muted'} label={'From start till today'} /> */}
          </Col>
          <Col lg="6" xl="3" sm="12">
            {/* <DashboardStatCard title={'Total Usage'} stats={statCards?.totalUsage} icon={'fas fa-clock'} iconColor={'text-muted'} label={'From start till today'} /> */}
          </Col>
          <Col lg="6" xl="3" sm="12">
            {/* <DashboardStatCard title={'Total Students'} stats={statCards?.totalStudents} icon={'fas fa-users'} iconColor={'text-muted'} label={'From start till today'} /> */}
            {/* <TransparentStatCard title={'Total Students'} stats={173} icon={'fas fa-users'} iconColor={'text-white'} label={'From start till today'} /> */}
          </Col>
        </Row>
      </Container>

      <Container className="mt-3" fluid>
        <Row>
          <Col className="mb-5 mb-xl-0" xl="12">
            <Card className="shadow"> {/* bg-gradient-default */}
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    {/* <h6 className="text-uppercase text-light ls-1 mb-1">
                      Overview
                    </h6> */}
                    <h3 className="mb-0 text-muted"><i className="fas fa-clipboard-list mr-1"></i>Overall Stats</h3>
                  </div>
                  <div className="col">
                    {/* FILTER FOR GRADE-SECTION SELECTION */}
                    <Dropdown size="sm" isOpen={gradeDropdownOpen} direction="left" toggle={gradeDropdownToggle} className="float-right">
                      <DropdownToggle caret>
                        {selectedGrade ? <span>Selected Grade: <span className="text-primary">{selectedGrade?.grade_section_id?.grade?.name} - {selectedGrade?.grade_section_id?.section?.type}</span></span> : 'Select Grade'}
                      </DropdownToggle>
                      <DropdownMenu style={{ maxHeight: 300, overflow: 'auto' }}>
                        {
                          gradeSectionInfo?.length !== 0 ? gradeSectionInfo?.map((each, index) => (
                            <DropdownItem key={index} onClick={() => setSelectedGrade(each)}>
                              <span className="d-flex justify-content-between align-items-center">
                                {each?.grade_section_id?.grade?.name} - {each?.grade_section_id?.section?.type}
                                {selectedGrade?.grade_section_id?.id === each?.grade_section_id?.id && <i className="fas fa-check-circle text-primary"></i>}
                              </span>
                            </DropdownItem>
                          )) : <span className="small text-muted text-center">No Grades Available</span>
                        }
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                {/* APP USAGE STATS */}
                {/* <div className="d-flex justify-content-end">
                  <Button
                    size="sm"
                    color={activeNav === 1 ? 'primary' : 'secondary'}
                    onClick={(e) => toggleLineChartNavs(e, 1)}>Last 7 Days</Button>
                  <Button
                    size="sm"
                    color={activeNav === 2 ? 'primary' : 'secondary'}
                    onClick={(e) => toggleLineChartNavs(e, 2)}>Last 30 Days</Button>
                </div>
                {!isStatLoading ? <Row className="chart">
                  <Col md={9} sm={12}>
                    <LineChart chartData={appUsageChartData?.data} chartLabels={appUsageChartData?.labels} title={"Usage In Minutes"} name={"Minutes"} />
                    <AreaChart chartData={appUsageChartData?.data} chartLabels={appUsageChartData?.labels} title={"App Usage In Minutes"} name={"Minutes"} />
                    <Column chartData={appUsageChartData?.data} chartLabels={appUsageChartData?.labels} name={"Usage(minutes)"} title={"App Usage"} />
                  </Col>
                  <Col md={3} sm={12} className="d-flex justify-content-center align-items-center">
                    <div>
                      <div>
                        <h1 className="mb-0 font-weight-bold">{Math.floor(appUsageChartData?.data?.reduce((accu, each) => accu += each, 0) / 60)
                          + ':' +
                          appUsageChartData?.data?.reduce((accu, each) => accu += each, 0) % 60} hrs</h1>
                        <p className="text-muted font-weight-bold">Total usage</p>
                      </div>
                      <div className="mt-5">
                        <h1 className="mb-0">{appUsageChartData?.labels[appUsageChartData?.data?.findIndex((e) => e === Math.max.apply(Math, appUsageChartData?.data))]}</h1> 
                        <p className="text-muted font-weight-bold">Busiest day</p>
                      </div>
                    </div>
                  </Col>
                </Row> : <div className="mt-7"><DotsSpinner /></div>} */}

                {/* APP USAGE STATS */}
                {/* {selectedGrade?.grade_section_id?.id ? <AppUsage
                  gradeSectionId={selectedGrade?.grade_section_id?.id}
                  chartType={'column'}
                /> : <></>} */}

                <hr />

                {/* PERFORMANCE & PROGRESS STATS */}
                {!isPerformanceLoading ? <Row className="mt-3">
                  <Col md={4} sm={12} className="mb-5 mb-xl-0">
                    <Card className="border-0">
                      <CardBody>
                        <RadialBar
                          chartData={overallPerformance}
                          chartLabels={['Performance', 'Progress']}
                          title={"Overall Grade Performance"}
                          height={300}
                        />
                        <div className="d-flex justify-content-center">
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 400 }}>
                              <i className="fas fa-square mr-1" style={{ color: '#055DC9' }}></i> 
                              Performance: {overallPerformance && overallPerformance[0]}%
                            </span>
                          </div>
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 400 }}>
                              <i className="fas fa-square ml-2 mr-1" style={{ color: '#008000' }}></i> 
                              Progress: {overallPerformance && overallPerformance[1]}%
                            </span>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={8} sm={12} className="mb-5 mb-xl-0">
                    <Card className="border-0">
                      <CardBody>
                        {/* <Column
                          chartData={gradeWisePerformance?.map((each) => Math.round(Number(each?.performance)))} chartLabels={gradeWisePerformance?.map((each) => each?.name)}
                          name={"Percentage"}
                          title={"Subject Wise Performance"}
                          legend={true} /> */}
                        <PairedColumnChart
                          firstSeriec={{
                            name: 'Course Progress',
                            data: gradeWisePerformance?.progress
                          }}
                          secondSeries={{
                            name: 'Assessment Performance',
                            data: gradeWisePerformance?.performance
                          }}
                          categories={gradeWisePerformance?.labels}
                          title={"Subject Wise Performance/Progress"}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                </Row> : <DotsSpinner />}
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </React.Fragment>
  );
};

export default Index;
